import type { DateTime } from 'luxon'

import type { PqMarketProgram } from '@/features/prequalification/localControl/types'

/**
 * Lifecycle phase of a single PQ run. Mirrors the backend `PqPhase` enum
 * (and the controller protocol's `PqTestState`). The const array is the single
 * source of truth so the union and the runtime guard (`isPqPhase`) can't drift.
 */
export const PQ_PHASES = ['SCHEDULED', 'ENQUEUED', 'STARTED', 'PASSED', 'CANCELLED', 'FAILED'] as const

export type PqPhase = (typeof PQ_PHASES)[number]

/** Narrows an unvalidated backend value to a known `PqPhase`. */
export function isPqPhase(value: unknown): value is PqPhase {
  return typeof value === 'string' && (PQ_PHASES as readonly string[]).includes(value)
}

/**
 * A UI-facing phase: either a real run phase or the synthetic `NOT_STARTED` that
 * stands for "no runs yet". `NOT_STARTED` never comes from the backend — it is
 * produced only by `aggregatePhase` over an empty run set — so `SCHEDULED` stays
 * reserved for runs actually scheduled to start at a future time.
 */
export type AggregatePhase = PqPhase | 'NOT_STARTED'

/**
 * Scope of a prequalification section. Only `local-control` is implemented in
 * v1; `cloud` is intentionally reserved so the cloud PQ flow (currently driven
 * from Activation Group details) can be folded in later without restructuring
 * the list/detail pages or the row contract below.
 */
export type PqScope = 'local-control' | 'cloud'

/**
 * A market program the resource set has touched, paired with its aggregate phase.
 * Lets the list grid show *outcome* per market (passed / running / failed),
 * not merely that a market was attempted.
 */
export type PqMarketStatus = {
  market: PqMarketProgram
  /** Aggregate phase across the resource set's runs in this market program. */
  phase: AggregatePhase
}

/**
 * Normalized row model consumed by the top-level `/prequalifications` list grid.
 *
 * This is the stable seam for the "fold cloud in later" requirement: a future
 * cloud source only needs to produce rows of this shape for the same grid to
 * render them. For local-control, one grid row === one backend *resource set*.
 */
export type PrequalificationResourceSet = {
  /** Backend resource set id. */
  id: string
  scope: PqScope
  /** Human-readable resource set name — the row's primary identity. */
  name: string
  /** Number of resources in the resource set. */
  resourceCount: number
  /** Market programs the resource set has runs in, each with its aggregate phase (empty until a test is triggered). */
  markets: PqMarketStatus[]
  /** Aggregate phase computed from the resource set's runs (`NOT_STARTED` when it has none). */
  phase: AggregatePhase
  requestedAt: DateTime
  startAt: DateTime | null
  lastStatusAt: DateTime | null
}

const TERMINAL_PHASES: ReadonlySet<PqPhase> = new Set<PqPhase>(['PASSED', 'CANCELLED', 'FAILED'])

export function isTerminalPhase(phase: PqPhase): boolean {
  return TERMINAL_PHASES.has(phase)
}

/**
 * Collapses the phases of a resource set's runs into a single aggregate phase for
 * the list grid and the detail header. Active phases win over terminal ones so
 * an in-flight resource set never looks finished; among terminal-only resource sets a
 * FAILED run dominates a PASSED one.
 *
 * The backend does not expose a parent/aggregate phase yet (deferred, LC-197),
 * so this is computed client-side from the run rows. With no runs the result is
 * `NOT_STARTED` rather than `SCHEDULED` — an untouched resource set hasn't been
 * scheduled, it simply hasn't run.
 */
export function aggregatePhase(phases: PqPhase[]): AggregatePhase {
  if (phases.length === 0) return 'NOT_STARTED'

  const priority: PqPhase[] = ['STARTED', 'ENQUEUED', 'SCHEDULED', 'FAILED', 'CANCELLED', 'PASSED']

  for (const phase of priority) {
    if (phases.includes(phase)) return phase
  }

  return 'PASSED'
}
